<template>
  <div class="table">
    <div class="detail-card">
      <div class="table-row">
        <div class="table-header">{{ $t('Date') }} | {{ $t('Time') }}</div>
        <div class="table-cell">{{ ticketDate }} | {{ ticketTime }}</div>
      </div>
      <div class="table-row">
        <div class="table-header">{{ $t('Game') }}</div>
        <div class="table-cell">{{ gameLabel }}</div>
      </div>
      <div class="table-row">
        <div class="table-header">{{ $t('Ante') }}</div>
        <div class="table-cell">
          {{ `${currencySymbol} ${ticket.taxAmount}` }}
        </div>
      </div>
      <!-- Multi-selection ticket: its total stake; the selections are listed below. -->
      <div v-if="isMulti" class="table-row">
        <div class="table-header">{{ $t('Total bet') }}</div>
        <div class="table-cell bet">
          <span class="bets-count">{{ subTickets.length }} {{ $t('Bets') }}</span>
          <span class="currency">
            {{ `${currencySymbol} ${formatAmount(ticket.amount)}` }}
          </span>
        </div>
      </div>
      <div v-else class="table-row">
        <div class="table-header">{{ $t('Bet') }}</div>
        <div class="table-cell bet">
          <img v-if="ticket.selection === 'banker'" src="@/assets/svg/chart-icons/banker.svg" alt="Banker count" />
          <img v-if="ticket.selection === 'player'" src="@/assets/svg/chart-icons/player.svg" alt="Player count" />
          <img v-if="ticket.selection === 'draw'" src="@/assets/svg/chart-icons/draw.svg" alt="Draw count" />
          <!-- Tài Xỉu: the market's badge, as the bet history shows it. -->
          <TxMarketBadge v-if="iconMarket" :market="iconMarket" />
          <span v-else-if="textMarket" class="market-badge" :class="textMarket.className">
            {{ textMarket.label }}
          </span>
          <span class="currency">
            {{ `${currencySymbol} ${formatAmount(ticket.amount)}` }}
          </span>
        </div>
      </div>
      <div v-if="!isMulti" class="table-row">
        <div class="table-header">{{ $t('Odds') }}</div>
        <div class="table-cell">{{ oddsLabel }}</div>
      </div>
      <div class="table-row table-row--result">
        <div class="table-header">{{ $t('Results') }}</div>
        <div class="table-cell result-cell">
          <template v-if="resultDice">
            <div class="dice-group-wrapper">
            <span class="dice-group">
              <img
                v-for="(die, index) in resultDice"
                :key="index"
                class="dice-face"
                :src="diceImage(die)"
                :alt="`Die ${index + 1}: ${die}`"
              />
            </span>
            <span class="dice-total">= {{ resultTotal }}</span>
            </div>
            <div class="dice-total-wrapper">
            <span class="market-badge" :class="bigSmallBadgeClass">{{ bigSmallLabel }}</span>
            <span class="market-badge" :class="oddEvenBadgeClass">{{ oddEvenLabel }}</span>
            </div>
          </template>
          <span
            v-else
            class="result"
            :class="[
              {
                cancelled: ticket.result === 'cancelled',
                void: ticket.result === 'void',
                [fight.result]: [FightStatus[12], FightStatus[13]].includes(fight.status),
              },
            ]"
          >
            {{
              $t(
                $capitalize(
                  ['cancelled', 'void'].includes(ticket.result)
                    ? ticket.result
                    : [FightStatus[12], FightStatus[13]].includes(fight.status)
                    ? fight.result
                    : ''
                )
              )
            }}
          </span>
        </div>
      </div>
      <div v-if="withCommission || hasCommission" class="table-row">
        <div class="table-header">{{ $t('Commission') }}</div>
        <div class="table-cell">
          {{ `${currencySymbol} ${ticket.commission}` }}
        </div>
      </div>
    </div>

    <!-- Multi-selection ticket: one line per selection, the winning ones marked. -->
    <div v-if="isMulti" class="selections">
      <div class="selections__head">
        <span class="selections__market">{{ $t('Selection') }}</span>
        <span class="selections__bet">{{ $t('Bet') }}</span>
        <span class="selections__odds">{{ $t('Odds') }}</span>
        <span class="selections__wl">W / L</span>
      </div>
      <div
        v-for="sub in subTickets"
        :key="sub.id"
        class="selections__row"
        :class="{ 'is-win': subIsWin(sub) }"
      >
        <span class="selections__market">
          <TxMarketBadge v-if="subIconMarket(sub)" :market="subIconMarket(sub)" />
          <span v-else class="market-badge" :class="subTextMarket(sub).className">
            {{ subTextMarket(sub).label }}
          </span>
          <span v-if="subIsWin(sub)" class="selections__win">{{ $t('Win') }}</span>
        </span>
        <span class="selections__bet">{{ sub.derived ? '—' : formatBetAmount(sub.amount) }}</span>
        <span class="selections__odds">{{ subOddsLabel(sub) }}</span>
        <span class="selections__wl" :class="subWlClass(sub)">{{ subWlText(sub) }}</span>
      </div>
    </div>

    <div class="table-row table-row--footer" v-if="!showEarnings">
      <div class="table-header">{{ $t('Win/lose') }}</div>
      <div
        class="table-cell"
        :class="[
          { win: !withCommission && ticket.winLossAmount > 0, lose: !withCommission && ticket.winLossAmount < 0 },
        ]"
      >
        {{ `${currencySymbol} ${ticket.winLossAmount}` }}
      </div>
    </div>
    <div class="table-row table-row--footer" v-else>
      <div class="table-header">{{ $t('Earning') }}</div>
      <div class="table-cell" :class="[{ win: ticket.earnings > 0, lose: ticket.earnings < 0 }]">
        {{ `${currencySymbol} ${formatAmountFixed(ticket.earnings)}` }}
      </div>
    </div>
    <div v-if="withCommission" class="table-row table-row--footer">
      <div class="table-header">{{ $t('Total') }}</div>
      <div
        class="table-cell"
        :class="[
          {
            win: Number(ticket.winLossAmount) + Number(ticket.commission) > 0,
            lose: Number(ticket.winLossAmount) + Number(ticket.commission) < 0,
          },
        ]"
      >
        {{ `${currencySymbol} ${Number(ticket.winLossAmount) + Number(ticket.commission)}` }}
      </div>
    </div>
    <div v-if="fight.replayUrl" class="bet-details-video-wrapper">
      <TCPlayer height="240" :srcURL="fight.replayUrl" />
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import { formatAmount, formatAmountFixed, formatBetAmount } from '@/utils/helpers';
import TCPlayer from '@/components/tc-player.vue';
import { FightStatus } from '@/utils/index.model';
import { formatTaiXiuOdds, taiXiuGameOdds, taiXiuIconMarket, taiXiuSelectionMarket } from '@/utils/tai-xiu-market';
import TxMarketBadge from '@/components/tai-xiu/tx-market-badge.vue';
import { getSubTickets, isMultiSelectionTicket, isTicketPending } from '@/utils/ticket-selections';

// Tài Xỉu's text markets; cockfight selections have their own icons above.
const TEXT_MARKETS = [
  { keys: ['tai', 'tài', 'big'], label: 'Big', className: 'is-tai' },
  { keys: ['xiu', 'xỉu', 'small'], label: 'Small', className: 'is-xiu' },
  { keys: ['le', 'lẻ', 'odd'], label: 'Odd', className: 'is-odd' },
  { keys: ['chan', 'chẵn', 'even'], label: 'Even', className: 'is-even' },
];
// A selection list has no room for the cockfight icons, so they are text badges there.
const COCKFIGHT_MARKETS = [
  { keys: ['banker'], label: 'Banker', className: 'is-banker' },
  { keys: ['player'], label: 'Player', className: 'is-player' },
  { keys: ['draw'], label: 'Draw', className: 'is-draw' },
];

export default {
  components: {
    TCPlayer,
    TxMarketBadge,
  },
  props: {
    ticket: {
      type: Object,
      default: () => {},
    },
  },
  setup() {
    return {
      FightStatus,
    };
  },
  computed: {
    ...mapGetters('general', ['config', 'member', 'timezone', 'withCommission', 'hasCommission']),
    currencySymbol() {
      return this.member.currencySymbol;
    },
    ticketDate() {
      return this.$moment(this.ticket.createdDateTime).tz(this.timezone).format('YYYY-MM-DD');
    },
    ticketTime() {
      return this.$moment(this.ticket.createdDateTime).tz(this.timezone).format('HH:mm:ss');
    },
    showEarnings() {
      return this.config.showEarnings;
    },
    fight() {
      return this.ticket.fight || {};
    },
    gameLabel() {
      const arena = (this.fight.matchDay || {}).arena || {};
      const name = (arena.name && arena.name.default) || arena.code || '';
      return arena.code ? `${name} - TABLE ${arena.code}` : name;
    },
    oddsLabel() {
      // Tài Xỉu tickets carry Euro odds; shown as game odds (1.97 -> 1 : 0.97).
      const gameOdds = this.isCockfightBet ? null : taiXiuGameOdds(this.ticket.odds);
      if (gameOdds !== null) return formatTaiXiuOdds(gameOdds);
      const value = Number(this.ticket.odds);
      return Number.isFinite(value) ? `1 : ${value}` : this.ticket.odds;
    },
    isCockfightBet() {
      return ['banker', 'player', 'draw'].includes(String(this.ticket.selection || '').toLowerCase());
    },
    isMulti() {
      return isMultiSelectionTicket(this.ticket);
    },
    subTickets() {
      return getSubTickets(this.ticket);
    },
    // Total / single / double / triple: an icon badge.
    iconMarket() {
      return this.isCockfightBet ? null : taiXiuIconMarket(this.ticket.betType, this.ticket.selection);
    },
    // Tài / Xỉu / Lẻ / Chẵn: a coloured text badge.
    textMarket() {
      const selection = String(this.ticket.selection || '').toLowerCase();
      const market = TEXT_MARKETS.find((item) => item.keys.includes(selection));
      return market ? { label: this.$t(market.label).toUpperCase(), className: market.className } : null;
    },
    resultDice() {
      const rawResult = this.fight.result;
      const values = Array.isArray(rawResult)
        ? rawResult.map(Number)
        : String(rawResult || '')
          .split(/[\s,|/-]+/)
          .filter(Boolean)
          .map(Number);
      return values.length === 3 && values.every((value) => Number.isInteger(value) && value >= 1 && value <= 6)
        ? values
        : null;
    },
    resultTotal() {
      return this.resultDice ? this.resultDice.reduce((sum, value) => sum + value, 0) : 0;
    },
    bigSmallLabel() {
      return this.resultTotal > 10 ? this.$t('Big').toUpperCase() : this.$t('Small').toUpperCase();
    },
    bigSmallBadgeClass() {
      return this.resultTotal > 10 ? 'is-tai' : 'is-xiu';
    },
    oddEvenLabel() {
      return this.resultTotal % 2 === 1 ? this.$t('Odd').toUpperCase() : this.$t('Even').toUpperCase();
    },
    oddEvenBadgeClass() {
      return this.resultTotal % 2 === 1 ? 'is-odd' : 'is-even';
    },
  },
  methods: {
    formatAmount,
    formatAmountFixed,
    formatBetAmount,
    diceImage(face) {
      return require(`@/assets/svg/dice-items/dice-${face}.svg`);
    },
    // A multi-selection ticket's selections, each badged and priced as a bet of its own.
    isCockfightSelection(sub) {
      return COCKFIGHT_MARKETS.some((market) => market.keys.includes(String(sub.selection || '').toLowerCase()));
    },
    subIconMarket(sub) {
      return this.isCockfightSelection(sub) ? null : taiXiuSelectionMarket(sub.betType, sub.selection);
    },
    subTextMarket(sub) {
      const selection = String(sub.selection || '').toLowerCase();
      const market = [...TEXT_MARKETS, ...COCKFIGHT_MARKETS].find((item) => item.keys.includes(selection));
      return market
        ? { label: this.$t(market.label).toUpperCase(), className: market.className }
        : { label: selection.toUpperCase() || '-', className: 'is-default' };
    },
    subOddsLabel(sub) {
      const gameOdds = this.isCockfightSelection(sub) ? null : taiXiuGameOdds(sub.odds);
      if (gameOdds !== null) return formatTaiXiuOdds(gameOdds).replace(/ /g, '');
      const value = Number(sub.odds);
      return Number.isFinite(value) ? `1:${value}` : sub.odds;
    },
    subIsWin(sub) {
      return !sub.derived && !isTicketPending(sub) && Number(sub.winLossAmount) > 0;
    },
    subWlText(sub) {
      if (isTicketPending(sub)) return this.$t('Pending');
      // Read from the ticket's summary: no W/L of its own.
      if (sub.derived) return '—';
      const value = Number(sub.winLossAmount) || 0;
      const amount = formatAmount(Math.abs(value));
      if (value > 0) return `+${amount}`;
      if (value < 0) return `-${amount}`;
      return amount;
    },
    subWlClass(sub) {
      if (isTicketPending(sub)) return 'is-pending';
      if (sub.derived) return {};
      const value = Number(sub.winLossAmount);
      return { win: value > 0, lose: value < 0 };
    },
  },
};
</script>
<style lang="scss" scoped>
.table {
  font-size: 12px;
  font-weight: 600;
  text-align: left;
  box-sizing: border-box;
  // Full width of the sidebar: no side gutter, so the rows run edge to edge.
  padding: 8px 0;

  .detail-card {
    overflow: hidden;
    border-top: 1px solid rgba(201, 162, 77, 0.35);
    border-bottom: 1px solid rgba(201, 162, 77, 0.35);
  }

  // Compact rows, so the whole ticket down to Win/lose fits without scrolling.
  .table-row {
    display: flex;
    box-sizing: border-box;
    min-height: 38px;
    align-items: center;

    .table-header {
      width: 40%;
      flex-shrink: 0;
      box-sizing: border-box;
      padding: 6px 12px;
      align-self: stretch;
      display: flex;
      align-items: center;
      background-color: #3a2e14;
      color: #efc87f;
      font-size: 13px;
      font-weight: 700;
      text-transform: uppercase;
    }

    .table-cell {
      flex: 1;
      min-width: 0;
      box-sizing: border-box;
      align-self: stretch;
      padding: 6px 12px;
      background-color: #15120e;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-end;
      flex-wrap: wrap;
      gap: 6px;
      color: #fff;
      font-size: 14px;
      font-weight: 500;
      text-align: right;

      &.win {
        color: #44f589;
      }
      &.lose {
        color: #e85050;
      }

      &.bet {
        img {
          margin-right: 4px;
        }
      }


      img {
        height: 16px;
        width: 16px;
      }
      .result {
        height: 20px;
        padding: 4px 6px;
        border-radius: 4px;
        text-transform: uppercase;
        display: flex;
        align-items: center;
        color: #d3febf;
        font-weight: 600;
        &.player {
          background-color: #0073e8;
        }

        &.banker {
          background-color: #c12828;
        }
        &.draw {
          background-color: #1fa052;
        }
        &.cancelled {
          color: #6c6030;
          background-color: #d8c7a0;
        }
        &.void {
          background-color: #3c3a37;
        }
      }
    }

    &--result {
      .table-cell {
        background-color: #16233d;
        // Dice and badges wrap onto two lines; keep both against the right edge
        // like every other value.
        justify-content: flex-end;
      }
    }

    &--footer {
      min-height: 0;
      margin-top: 8px;
      padding: 8px 12px 0;
      border-top: 1px solid #c9a24d;

      .table-header {
        width: auto;
        padding: 0;
        background: transparent;
      }

      .table-cell {
        padding: 0;
        background: transparent;
        font-size: 16px;
        font-weight: 800;
      }
    }
  }

  // The bet's Tài Xỉu text market; colours match the bet history's badges.
  .table-cell.bet .market-badge {
    display: inline-flex;
    align-items: center;
    height: 24px;
    padding: 0 8px;
    border-radius: 4px;
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;

    &.is-tai {
      background: linear-gradient(160deg, #c33a2f, #a01f1f 55%, #6b1414);
    }

    &.is-xiu {
      background: linear-gradient(200deg, #3a86dc, #1f62b8 50%, #0d3f7a);
    }

    &.is-odd,
    &.is-even {
      background: linear-gradient(180deg, #24a35c, #157140);
    }
  }

  // Multi-selection ticket: "N BETS" beside the total stake.
  .bets-count {
    display: inline-flex;
    align-items: center;
    height: 20px;
    padding: 0 7px;
    border: 1px solid #8a6d2a;
    border-radius: 4px;
    color: #e0b456;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    white-space: nowrap;
  }

  // Multi-selection ticket: its selections (Multi-Selection Ticket Prototype v1.0).
  .selections {
    margin-top: 10px;

    &__head,
    &__row {
      display: grid;
      // Narrower than the prototype's 76/52/92px: this detail also opens in the
      // 296px desktop rail, where those left no room for the badge.
      grid-template-columns: minmax(0, 1fr) 60px 40px 76px;
      gap: 6px;
      align-items: center;
      padding: 0 12px;
    }

    &__head {
      padding-top: 9px;
      padding-bottom: 9px;
      background-color: #2a2414;
      color: #d6ae5c;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }

    &__row {
      min-height: 44px;
      border-bottom: 1px solid #1b3358;
      color: #fff;
      font-size: 13px;
      font-weight: 500;

      &.is-win {
        background-color: rgba(53, 208, 127, 0.1);
      }
    }

    &__market {
      display: flex;
      align-items: center;
      gap: 8px;
      min-width: 0;
    }

    &__bet,
    &__wl {
      text-align: right;
    }

    &__odds {
      text-align: center;
    }

    // Spelled out: nested under `.table`, `&__row &__wl` compiled to
    // `.table .selections__row .table .selections__wl`, which never matched --
    // the W/L stayed white instead of green / red.
    .selections__row .selections__odds {
      color: #8ea0bd;
    }

    .selections__row .selections__wl {
      font-weight: 700;

      &.win {
        color: #44f589;
      }

      &.lose {
        color: #e85050;
      }

      &.is-pending {
        color: #8ea0bd;
        font-size: 11px;
        text-transform: uppercase;
      }
    }

    &__win {
      color: #35d07f;
      font-size: 9.5px;
      font-weight: 800;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }

    .market-badge {
      display: inline-flex;
      align-items: center;
      height: 24px;
      padding: 0 8px;
      border-radius: 4px;
      color: #fff;
      font-size: 11px;
      font-weight: 800;
      letter-spacing: 0.04em;
      white-space: nowrap;

      &.is-tai {
        background: linear-gradient(160deg, #c33a2f, #a01f1f 55%, #6b1414);
      }

      &.is-xiu {
        background: linear-gradient(200deg, #3a86dc, #1f62b8 50%, #0d3f7a);
      }

      &.is-odd,
      &.is-even {
        background: linear-gradient(180deg, #24a35c, #157140);
      }

      &.is-banker {
        background-color: #eb3323;
      }

      &.is-player {
        background-color: #3996e5;
      }

      &.is-draw {
        background-color: #0c836c;
      }

      &.is-default {
        background-color: #24475c;
        color: #b2d6ea;
      }
    }
  }

  .result-cell {
    .dice-group-wrapper,
    .dice-total-wrapper {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      gap: 4px;
    }
    .dice-group {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 4px;
    }

    .dice-face {
      width: 26px;
      height: 26px;
      margin: 0;
    }

    .dice-total {
      color: #fff;
      font-size: 15px;
      font-weight: 700;
    }

    .market-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      height: 24px;
      padding: 0 8px;
      border-radius: 4px;
      color: #fff;
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      white-space: nowrap;

      &.is-tai {
        background-color: #eb3323;
      }

      &.is-xiu {
        background-color: #0273e8;
      }

      &.is-odd {
        background-color: #0c836c;
      }

      &.is-even {
        background-color: #0c836c;
      }
    }
  }

  .bet-details-video-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 20px;
    position: relative;
    height: 240px;

    video {
      margin-bottom: 40px;
    }
  }

  /*
   * Phones: tighter rows, and the result's dice and badges share one line, so
   * the ticket down to Win/lose fits the popup without scrolling. The result
   * cell still wraps if a narrow phone cannot hold both.
   */
  @media (max-width: 600px) {
    padding: 4px 0;

    .table-row {
      min-height: 30px;

      .table-header {
        width: 34%;
        padding: 4px 12px;
        font-size: 12px;
      }

      .table-cell {
        padding: 4px 12px;
        gap: 4px 8px;
        font-size: 13px;
      }

      &--footer {
        margin-top: 4px;
        padding-top: 6px;

        .table-header {
          width: auto;
          padding: 0;
        }

        .table-cell {
          font-size: 15px;
        }
      }
    }

    .table-cell.bet .market-badge,
    .result-cell .market-badge {
      height: 22px;
      padding: 0 6px;
      font-size: 11px;
    }

    .result-cell {
      column-gap: 8px;

      .dice-group-wrapper,
      .dice-total-wrapper {
        gap: 3px;
      }

      .dice-group {
        gap: 3px;
      }

      .dice-face {
        width: 22px;
        height: 22px;
      }

      .dice-total {
        font-size: 14px;
      }
    }

    .bet-details-video-wrapper {
      margin-top: 8px;
    }
  }
}
</style>
